import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

@Injectable()
export class ImageService {
  private imagenes: BehaviorSubject<string[]> = new BehaviorSubject([]);

  private imgSeleccionada: BehaviorSubject<string> = new BehaviorSubject(null);

  /**
   * La función establece un array de URL de imágenes en la variable 'imagenes'
   * @param {string[]} imgUrls - El parámetro `imgUrls` es array de urls de imagenes.
   */
  setImagenes(imgUrls: string[]): void {
    this.imgSeleccionada.next(null);

    this.imagenes.next(imgUrls);
    if (this.imagenes.getValue()) {
      this.imgSeleccionada.next(this.imagenes.getValue()[0]);
    }
  }

  /**
   * La función devuelve el número de imágenes.
   * @returns {number} - El número de imágenes en "imagenes".
   * - Si "imagenes" no tiene valor, devuelve "undefined".
   */
  getNumImagenes(): number {
    if (this.imagenes.getValue()) {
      return this.imagenes.getValue().length;
    }
  }

  /**
   * La función setSeleccionada establece como imagen seleccionada (imgSeleccionada) la url
   * del índice proporcionado.
   * @param {string} imgUrl - Una cadena que representa la URL de la imagen a seleccionar.
   */
  setSeleccionada(index: number): void {
    this.imgSeleccionada.next(this.imagenes.getValue()[index]);
  }

  /**
   * La función devuelve un Observable que emite el valor de la variable imgSeleccionada.
   * @returns El método devuelve un Observable de tipo cadena.
   */
  getSeleccionada(): Observable<string> {
    return this.imgSeleccionada.asObservable();
  }

  /**
   * La función `getIndexActual()` devuelve el índice de la imagen seleccionada en la matriz de
   * imágenes.
   * @returns {number} La función `getIndexActual()` está devolviendo el índice del valor almacenado en
   * `this.imgSeleccionada` dentro del array `this.imagenes`.
   */
  getIndexActual(): number {
    return this.imagenes.getValue().indexOf(this.imgSeleccionada.value);
  }

  /**
   * La función "getImgAnterior" establece la imagen seleccionada en la imagen anterior de la lista, a
   * menos que la imagen actual sea la primera imagen, en cuyo caso se mantendrá la imagen actual.
   */
  getImgAnterior() {
    const actual: number = this.getIndexActual();
    if (actual === 0) {
      this.setSeleccionada(0);
      return;
    }
    this.setSeleccionada(actual - 1);
  }

  /**
   * La función `getImgSiguiente` selecciona la siguiente imagen de la lista, a menos que la imagen
   * actual sea la última imagen, en cuyo caso se mantendrá la imagen actual.
   */
  getImgSiguiente() {
    const actual: number = this.getIndexActual();
    if (actual === this.imagenes.getValue().length - 1) {
      this.setSeleccionada(this.imagenes.getValue().length - 1);
      return;
    }
    this.setSeleccionada(actual + 1);
  }

  /**
   * La función "getImagenes" devuelve un array de rutas de imagenes.
   * @returns Un array de rutas de imagenes.
   */
  getImagenes(): Observable<string[]> {
    return this.imagenes.asObservable();
  }
}
